/**
 * Frontend client for the Map-Lens notes Worker (services/notes-api/).
 * The base URL is configured at build time via VITE_NOTES_API_URL; in dev
 * it defaults to the local `wrangler dev` address.
 */

const API_URL = import.meta.env.VITE_NOTES_API_URL ?? 'http://127.0.0.1:8787';

export interface PublicNote {
  id: string;
  landmarkId: string;
  nickname: string;
  body: string;
  createdAt: number;
}

export interface AdminNote extends PublicNote {
  /** unix seconds, or null for active notes */
  deletedAt: number | null;
}

export class NotesApiError extends Error {
  constructor(public readonly status: number, message: string, public readonly field?: string) {
    super(message);
    this.name = 'NotesApiError';
  }
}

async function parseError(res: Response): Promise<NotesApiError> {
  let body: { error?: string; field?: string; reason?: string } = {};
  try {
    body = (await res.json()) as typeof body;
  } catch {
    // ignore — leave body empty
  }
  const message = [body.error, body.field, body.reason].filter(Boolean).join(' / ') || `HTTP ${res.status}`;
  return new NotesApiError(res.status, message, body.field);
}

export async function listNotes(landmarkId: string): Promise<PublicNote[]> {
  const url = `${API_URL}/api/notes?landmarkId=${encodeURIComponent(landmarkId)}`;
  const res = await fetch(url);
  if (!res.ok) throw await parseError(res);
  const data = (await res.json()) as { notes?: PublicNote[] };
  return data.notes ?? [];
}

export async function createNote(input: {
  landmarkId: string;
  nickname: string;
  body: string;
  turnstileToken: string;
}): Promise<PublicNote> {
  const res = await fetch(`${API_URL}/api/notes`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) throw await parseError(res);
  return (await res.json()) as PublicNote;
}

export async function listAllNotesAdmin(adminToken: string): Promise<AdminNote[]> {
  const res = await fetch(`${API_URL}/api/admin/notes`, {
    headers: { Authorization: `Bearer ${adminToken}` },
  });
  if (!res.ok) throw await parseError(res);
  const data = (await res.json()) as { notes?: AdminNote[] };
  return data.notes ?? [];
}

export async function deleteNote(id: string, adminToken: string): Promise<void> {
  const res = await fetch(`${API_URL}/api/admin/notes/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${adminToken}` },
  });
  if (!res.ok) throw await parseError(res);
}
